import React, { useState } from 'react';

const EmployeeMappingModal = ({ 
  isOpen, 
  unmatchedRecords = [], 
  validRecords = [],
  company,
  onConfirm, 
  onCancel 
}) => {
  const [mappings, setMappings] = useState({});
  const [searchTerms, setSearchTerms] = useState({});

  // Ensure we have valid arrays
  const safeUnmatchedRecords = Array.isArray(unmatchedRecords) ? unmatchedRecords : [];
  const safeValidRecords = Array.isArray(validRecords) ? validRecords : [];

  const handleEmployeeSelect = (csvEmployeeId, selectedEmployee) => {
    setMappings(prev => ({
      ...prev,
      [csvEmployeeId]: {
        employeeNumber: selectedEmployee.employee_number,
        employeeName: selectedEmployee.employee_name,
        newEmployeeId: csvEmployeeId
      }
    }));
  };

  const handleSearchChange = (csvEmployeeId, searchTerm) => {
    setSearchTerms(prev => ({
      ...prev,
      [csvEmployeeId]: searchTerm
    }));
  };

  const filterEmployees = (employees, searchTerm) => {
    if (!searchTerm) return employees;
    
    const term = searchTerm.toLowerCase();
    return employees.filter(emp => 
      emp.employee_name.toLowerCase().includes(term) ||
      emp.employee_number.toString().includes(term)
    );
  };

  const getAvailableEmployees = (record) => {
    // Get employees that don't already have the company's employee_id set
    const companyField = company === 'KF' ? 'employee_id_kf' : 'employee_id_koo';
    
    // Ensure available_employees is an array
    const availableEmployees = Array.isArray(record?.available_employees) ? record.available_employees : [];
    
    return availableEmployees.filter(emp => 
      !emp[companyField] || emp[companyField] === '' || emp[companyField] === null
    );
  };

  const handleConfirm = () => {
    const mappingArray = Object.values(mappings);
    onConfirm({
      mappings: mappingArray,
      cancelled: false
    });
  };

  const handleCancel = () => {
    onCancel({
      cancelled: true
    });
  };

  const allMapped = safeUnmatchedRecords.every(record => mappings[record.employee_id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Map Unmatched Employees</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary */}
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <h3 className="font-semibold text-yellow-800 mb-2">Employee ID Validation Results</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-green-600 font-medium">✓ Matched Records:</span> {safeValidRecords.length}
            </div>
            <div>
              <span className="text-red-600 font-medium">⚠ Unmatched Records:</span> {safeUnmatchedRecords.length}
            </div>
          </div>
          <p className="text-yellow-700 text-sm mt-2">
            The following employees from the CSV don't have matching {company} employee IDs in the database. 
            Please map them to existing employee records.
          </p>
        </div>

        {/* Unmatched Records */}
        <div className="overflow-y-auto max-h-96 mb-6">
          <div className="space-y-4">
            {safeUnmatchedRecords.map((record, index) => {
              const availableEmployees = getAvailableEmployees(record);
              const searchTerm = searchTerms[record.employee_id] || '';
              const filteredEmployees = filterEmployees(availableEmployees, searchTerm);
              const selectedMapping = mappings[record.employee_id];

              return (
                <div key={index} className="border border-gray-300 rounded-lg p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* CSV Employee Info */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">From CSV:</h4>
                      <div className="bg-gray-50 p-3 rounded">
                        <p><strong>Name:</strong> {record.csv_name}</p>
                        <p><strong>Employee ID:</strong> {record.employee_id}</p>
                        <p><strong>Hours:</strong> {record.total_hours}</p>
                        <p><strong>Gross Pay:</strong> ${record.gross_pay}</p>
                      </div>
                    </div>

                    {/* Mapping Selection */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Map to Employee:</h4>
                      
                      {/* Search */}
                      <input
                        type="text"
                        placeholder="Search by name or employee number..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(record.employee_id, e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded mb-2 text-sm"
                      />

                      {/* Employee Selection */}
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded">
                        {filteredEmployees.length === 0 ? (
                          <div className="p-3 text-gray-500 text-sm">
                            {availableEmployees.length === 0 
                              ? 'No available employees (all have existing IDs)' 
                              : 'No employees match search criteria'
                            }
                          </div>
                        ) : (
                          filteredEmployees.map((employee) => (
                            <div
                              key={employee.employee_number}
                              onClick={() => handleEmployeeSelect(record.employee_id, employee)}
                              className={`p-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 text-sm ${
                                selectedMapping?.employeeNumber === employee.employee_number
                                  ? 'bg-blue-100 border-blue-300'
                                  : ''
                              }`}
                            >
                              <div className="flex justify-between">
                                <span className="font-medium">{employee.employee_name}</span>
                                <span className="text-gray-500">#{employee.employee_number}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Selected Mapping */}
                      {selectedMapping && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                          <p><strong>Mapped to:</strong> {selectedMapping.employeeName}</p>
                          <p><strong>Will set {company} ID:</strong> {selectedMapping.newEmployeeId}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {Object.keys(mappings).length} of {safeUnmatchedRecords.length} employees mapped
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel Import
            </button>
            <button
              onClick={() => {
                // Skip unmatched records (treat them as non-employee rows)
                onConfirm({ mappings: {}, skipUnmatched: true });
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
            >
              Skip These Records
            </button>
            <button
              onClick={handleConfirm}
              disabled={!allMapped}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue Import ({Object.keys(mappings).length}/{safeUnmatchedRecords.length})
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-4 text-xs text-gray-500">
          <p><strong>Instructions:</strong></p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Map each employee to an existing employee record, OR</li>
            <li>Click "Skip These Records" if these are summary/total rows (not actual employees)</li>
            <li>Only employees without existing {company} IDs are shown</li>
            <li>The mapping will update the employee's {company} ID field</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmployeeMappingModal;